const SUMMARY_LABEL_KEYS = Object.freeze({
    marketValue: 'reports.summary.marketValue',
    costBasis: 'reports.summary.costBasis',
    totalCost: 'reports.summary.costBasis',
    realizedGainLoss: 'reports.summary.realizedGainLoss',
    unrealizedGainLoss: 'reports.summary.unrealizedGainLoss',
    totalGainLoss: 'reports.summary.totalGainLoss',
    totalPnl: 'reports.summary.totalPnl',
    totalDebit: 'reports.summary.totalDebit',
    totalCredit: 'reports.summary.totalCredit',
    totalIncome: 'reports.summary.totalIncome',
    totalExpenses: 'reports.summary.totalExpenses',
    netProfit: 'reports.summary.netProfit',
    count: 'reports.summary.count',
    total: 'reports.summary.total',
    openingBalance: 'reports.summary.openingBalance',
    closingBalance: 'reports.summary.closingBalance',
    transactionCount: 'reports.summary.transactionCount',
    valuationCoverageRatio: 'reports.summary.valuationCoverageRatio',
    missingValuationCount: 'reports.summary.missingValuationCount',
    reversedCount: 'reports.summary.reversedCount',
    clientCount: 'reports.summary.clientCount',
    portfolioCount: 'reports.summary.portfolioCount',
    breaks: 'reports.summary.breaks',
    openBreaks: 'reports.summary.openBreaks',
    positions: 'reports.summary.positions',
    returnOnCostPct: 'portfolioPerformance.columns.returnOnCostPct',
});
const SUMMARY_VALUE_KINDS = Object.freeze({
    marketValue: 'MONEY',
    costBasis: 'MONEY',
    totalCost: 'MONEY',
    realizedGainLoss: 'MONEY',
    unrealizedGainLoss: 'MONEY',
    totalGainLoss: 'MONEY',
    totalPnl: 'MONEY',
    totalDebit: 'MONEY',
    totalCredit: 'MONEY',
    totalIncome: 'MONEY',
    totalExpenses: 'MONEY',
    netProfit: 'MONEY',
    openingBalance: 'MONEY',
    closingBalance: 'MONEY',
    valuationCoverageRatio: 'PERCENT',
    returnOnCostPct: 'PERCENT',
    count: 'NUMBER',
    total: 'NUMBER',
    transactionCount: 'NUMBER',
    missingValuationCount: 'NUMBER',
    reversedCount: 'NUMBER',
    clientCount: 'NUMBER',
    portfolioCount: 'NUMBER',
    breaks: 'NUMBER',
    openBreaks: 'NUMBER',
    positions: 'NUMBER',
});
const SENSITIVE_FIELDS = new Set([
    'accountNumber',
    'custodyAccountNumber',
    'beneficiaryAccountNumber',
    'taxIdentifier',
    'pan',
]);
const TECHNICAL_FIELDS = new Set([
    'id',
    'organizationId',
    'branchId',
    'clientAccountId',
    'portfolioAccountId',
    'positionFactId',
    'valuationFactId',
    'version',
    'reportRunId',
    'outputId',
    'requestFingerprint',
    'sourceDigest',
    'snapshotHash',
]);
const EXPLICIT_GROUPS = Object.freeze({
    'report.portfolio.holdings': 'PORTFOLIO',
    'report.portfolio.performance-nepal': 'PORTFOLIO',
    'report.portfolio.realized-gain-loss': 'PORTFOLIO',
    'report.portfolio.unrealized-gain-loss': 'PORTFOLIO',
    'report.portfolio.cash-position': 'PORTFOLIO',
    'report.portfolio.investment-income': 'PORTFOLIO',
    'report.portfolio.tax-lots-cost-basis': 'PORTFOLIO',
    'report.portfolio.corporate-actions': 'PORTFOLIO',
    'report.portfolio.valuation-quality': 'PORTFOLIO',
    'report.portfolio.allocation-exposure': 'PORTFOLIO',
    'report.portfolio.custody-evidence': 'PORTFOLIO',
    'report.portfolio.cash-movement': 'TRANSACTIONS',
    'report.portfolio.fees-expenses': 'ACCOUNTING',
    'report.accounting.general-ledger': 'ACCOUNTING',
    'report.management.portfolio-summary': 'MANAGEMENT',
    'report.tax.cgt-analytics': 'TAX',
    'report.client.holdings': 'CLIENT',
    'report.client.portfolio.performance-nepal': 'CLIENT',
    'report.client.custody-evidence': 'CLIENT',
    'report.client.statement': 'CLIENT',
    'report.organization.client-statements': 'CLIENT',
    'report.transaction.activity': 'TRANSACTIONS',
    'report.accounting.trial-balance': 'ACCOUNTING',
    'report.accounting.financial-statements': 'ACCOUNTING',
    'report.accounting.profit-and-loss': 'ACCOUNTING',
    'report.reconciliation.breaks': 'RECONCILIATION',
    'report.regulatory.sebon-monthly': 'REGULATORY_OPERATIONS',
    'report.regulatory.ird-cgt': 'TAX',
    'report.regulatory.nrb-quarterly': 'REGULATORY_OPERATIONS',
    'report.regulatory.fiu-aml-ctf': 'REGULATORY_OPERATIONS',
});
const HUB_PRESENTATION = Object.freeze({
    'report.portfolio.holdings': { order: 10, prominence: 'FEATURED', keywords: ['positions', 'valuation', 'investments'] },
    'report.portfolio.performance-nepal': { order: 20, prominence: 'FEATURED', keywords: ['performance', 'returns', 'twr', 'pnl'] },
    'report.portfolio.realized-gain-loss': { order: 30, prominence: 'FEATURED', keywords: ['realized', 'gain', 'loss', 'disposals', 'sales', 'cost basis'] },
    'report.portfolio.unrealized-gain-loss': { order: 40, prominence: 'FEATURED', keywords: ['unrealized', 'gain', 'loss', 'valuation', 'mark to market'] },
    'report.portfolio.allocation-exposure': { order: 50, prominence: 'FEATURED', keywords: ['allocation', 'exposure', 'concentration', 'sector', 'currency'] },
    'report.portfolio.cash-position': { order: 60, prominence: 'STANDARD', keywords: ['cash', 'balance', 'currency', 'liquidity'] },
    'report.portfolio.investment-income': { order: 70, prominence: 'STANDARD', keywords: ['income', 'dividend', 'coupon', 'interest'] },
    'report.portfolio.tax-lots-cost-basis': { order: 80, prominence: 'STANDARD', keywords: ['tax lots', 'cost basis', 'acquisition', 'remaining quantity'] },
    'report.portfolio.corporate-actions': { order: 90, prominence: 'STANDARD', keywords: ['corporate actions', 'dividend', 'rights', 'bonus', 'split'] },
    'report.portfolio.valuation-quality': { order: 100, prominence: 'STANDARD', keywords: ['valuation', 'missing price', 'data quality', 'coverage'] },
    'report.portfolio.custody-evidence': { order: 110, prominence: 'STANDARD', keywords: ['custody', 'cdsc', 'evidence'] },
    'report.portfolio.cash-movement': { order: 45, prominence: 'STANDARD', keywords: ['cash', 'deposit', 'withdrawal', 'transfer', 'movement'] },
    'report.transaction.activity': { order: 50, prominence: 'FEATURED', keywords: ['transactions', 'activity', 'purchases', 'sales', 'transfers'] },
    'report.accounting.profit-and-loss': { order: 10, prominence: 'FEATURED', keywords: ['pnl', 'profit', 'loss', 'income', 'expenses'] },
    'report.accounting.trial-balance': { order: 20, prominence: 'STANDARD', keywords: ['trial balance', 'ledger', 'accounting'] },
    'report.accounting.general-ledger': { order: 30, prominence: 'STANDARD', keywords: ['general ledger', 'journal', 'account', 'debit', 'credit'] },
    'report.accounting.financial-statements': { order: 40, prominence: 'STANDARD', keywords: ['financial statements', 'balance sheet', 'cash flow'] },
    'report.portfolio.fees-expenses': { order: 50, prominence: 'STANDARD', keywords: ['fees', 'expenses', 'management fee', 'performance fee'] },
    'report.tax.cgt-analytics': { order: 10, prominence: 'FEATURED', keywords: ['cgt', 'tax', 'taxable gain', 'holding period', 'tax rule'] },
    'report.reconciliation.breaks': { order: 10, prominence: 'FEATURED', keywords: ['reconciliation', 'breaks', 'exceptions'] },
    'report.management.portfolio-summary': { order: 10, prominence: 'FEATURED', keywords: ['management', 'portfolio summary', 'clients', 'aum', 'valuation'] },
    'report.organization.client-statements': { order: 10, prominence: 'FEATURED', keywords: ['client', 'statement', 'portfolio statement'] },
    'report.client.statement': { order: 10, prominence: 'FEATURED', keywords: ['statement', 'portfolio statement'] },
    'report.client.portfolio.performance-nepal': { order: 20, prominence: 'STANDARD', keywords: ['performance', 'returns'] },
    'report.client.holdings': { order: 30, prominence: 'STANDARD', keywords: ['investments', 'holdings'] },
    'report.client.custody-evidence': { order: 40, prominence: 'STANDARD', keywords: ['account', 'custody', 'cdsc'] },
});
const PRIMARY_SUMMARY_FIELDS = Object.freeze({
    'report.portfolio.holdings': ['marketValue', 'costBasis', 'unrealizedGainLoss', 'positions'],
    'report.client.holdings': ['marketValue', 'unrealizedGainLoss', 'positions'],
    'report.portfolio.realized-gain-loss': ['transactionCount'],
    'report.portfolio.unrealized-gain-loss': ['positions'],
    'report.portfolio.cash-position': ['positions'],
    'report.portfolio.cash-movement': ['transactionCount', 'reversedCount'],
    'report.portfolio.investment-income': ['transactionCount'],
    'report.portfolio.fees-expenses': ['transactionCount'],
    'report.portfolio.tax-lots-cost-basis': ['positions'],
    'report.portfolio.corporate-actions': ['transactionCount'],
    'report.portfolio.valuation-quality': ['positions'],
    'report.portfolio.allocation-exposure': ['positions'],
    'report.management.portfolio-summary': ['clientCount', 'portfolioCount', 'positions'],
    'report.tax.cgt-analytics': ['transactionCount'],
    'report.transaction.activity': ['transactionCount', 'realizedGainLoss', 'unrealizedGainLoss', 'valuationCoverageRatio'],
    'report.accounting.trial-balance': ['totalDebit', 'totalCredit', 'closingBalance'],
    'report.accounting.general-ledger': ['transactionCount', 'totalDebit', 'totalCredit'],
    'report.accounting.profit-and-loss': ['totalIncome', 'totalExpenses', 'netProfit'],
    'report.reconciliation.breaks': ['breaks', 'openBreaks'],
    'report.portfolio.performance-nepal': ['marketValue', 'totalGainLoss', 'returnOnCostPct'],
    'report.client.portfolio.performance-nepal': ['marketValue', 'totalGainLoss', 'returnOnCostPct'],
});
function groupFor(reportId, category) {
    const explicit = EXPLICIT_GROUPS[reportId];
    if (explicit)
        return explicit;
    const normalizedCategory = String(category ?? '').toUpperCase();
    if (normalizedCategory === 'TAX')
        return 'TAX';
    if (normalizedCategory === 'ACCOUNTING')
        return 'ACCOUNTING';
    if (normalizedCategory === 'RECONCILIATION')
        return 'RECONCILIATION';
    if (normalizedCategory === 'TRANSACTIONS')
        return 'TRANSACTIONS';
    if (normalizedCategory === 'MANAGEMENT')
        return 'MANAGEMENT';
    if (normalizedCategory === 'REGULATORY')
        return 'REGULATORY_OPERATIONS';
    return 'PORTFOLIO';
}
function groupLabelKey(group) {
    return {
        PORTFOLIO: 'reports.groups.portfolio',
        CLIENT: 'reports.groups.client',
        TRANSACTIONS: 'reports.groups.transactions',
        TAX: 'reports.groups.tax',
        ACCOUNTING: 'reports.groups.accounting',
        RECONCILIATION: 'reports.groups.reconciliation',
        MANAGEMENT: 'reports.groups.management',
        REGULATORY_OPERATIONS: 'reports.groups.regulatoryOperations',
    }[group];
}
function scopeLabelKey(scope) {
    const normalized = String(scope ?? 'ORGANIZATION').toUpperCase();
    return {
        BRANCH: 'reports.scopes.branch',
        CLIENT: 'reports.scopes.client',
        PORTFOLIO: 'reports.scopes.portfolio',
        ORGANIZATION: 'reports.scopes.organization',
        PLATFORM: 'reports.scopes.platform',
    }[normalized] ?? 'reports.scopes.organization';
}
function exportFormats(_reportId, _group) {
    return ['CSV'];
}
function hubFor(reportId, group) {
    return HUB_PRESENTATION[reportId] ?? {
        order: 500,
        prominence: 'STANDARD',
        keywords: [group.toLowerCase()],
    };
}
const CLIENT_STATEMENT_BLOCKS = [
    ['portfolioValueByCurrency', 'MONEY_BY_CURRENCY'],
    ['cashByCurrency', 'MONEY_BY_CURRENCY'],
    ['feesByCurrency', 'MONEY_BY_CURRENCY'],
    ['taxByCurrency', 'MONEY_BY_CURRENCY'],
    ['quality', 'DETAIL_GROUP'],
];
function summaryBlocksFor(reportId, summary) {
    const configured = {
        'report.portfolio.realized-gain-loss': [
            ['saleProceedsByCurrency', 'MONEY_BY_CURRENCY'],
            ['costBasisByCurrency', 'MONEY_BY_CURRENCY'],
            ['deductibleChargesByCurrency', 'MONEY_BY_CURRENCY'],
            ['realizedGainLossByCurrency', 'MONEY_BY_CURRENCY'],
            ['taxableGainByCurrency', 'MONEY_BY_CURRENCY'],
            ['capitalGainTaxByCurrency', 'MONEY_BY_CURRENCY'],
        ],
        'report.portfolio.unrealized-gain-loss': [
            ['totalCostByCurrency', 'MONEY_BY_CURRENCY'],
            ['marketValueByCurrency', 'MONEY_BY_CURRENCY'],
            ['unrealizedGainLossByCurrency', 'MONEY_BY_CURRENCY'],
        ],
        'report.tax.cgt-analytics': [
            ['taxableGainByCurrency', 'MONEY_BY_CURRENCY'],
            ['capitalGainTaxByCurrency', 'MONEY_BY_CURRENCY'],
        ],
        'report.portfolio.cash-position': [
            ['cashByCurrency', 'MONEY_BY_CURRENCY'],
        ],
        'report.portfolio.cash-movement': [
            ['depositsByCurrency', 'MONEY_BY_CURRENCY'],
            ['withdrawalsByCurrency', 'MONEY_BY_CURRENCY'],
            ['transfersByCurrency', 'MONEY_BY_CURRENCY'],
        ],
        'report.portfolio.investment-income': [
            ['grossIncomeByCurrency', 'MONEY_BY_CURRENCY'],
            ['withholdingByCurrency', 'MONEY_BY_CURRENCY'],
            ['netIncomeByCurrency', 'MONEY_BY_CURRENCY'],
        ],
        'report.portfolio.fees-expenses': [
            ['netFeesByCurrency', 'MONEY_BY_CURRENCY'],
            ['taxByCurrency', 'MONEY_BY_CURRENCY'],
            ['grossFeesByCurrency', 'MONEY_BY_CURRENCY'],
        ],
        'report.portfolio.tax-lots-cost-basis': [
            ['remainingCostBasisByCurrency', 'MONEY_BY_CURRENCY'],
        ],
        'report.portfolio.valuation-quality': [
            ['affectedMarketValueByCurrency', 'MONEY_BY_CURRENCY'],
        ],
        'report.portfolio.allocation-exposure': [
            ['marketValueByCurrency', 'MONEY_BY_CURRENCY'],
        ],
        'report.management.portfolio-summary': [
            ['marketValueByCurrency', 'MONEY_BY_CURRENCY'],
            ['totalGainLossByCurrency', 'MONEY_BY_CURRENCY'],
        ],
        'report.transaction.activity': [
            ['totalAmountByCurrency', 'MONEY_BY_CURRENCY'],
            ['countByStatus', 'STATUS_BREAKDOWN'],
            ['countByPostingStatus', 'STATUS_BREAKDOWN'],
        ],
        'report.reconciliation.breaks': [
            ['exposureByCurrency', 'RECONCILIATION_EXPOSURE'],
            ['adjustmentsByStatus', 'STATUS_BREAKDOWN'],
        ],
        'report.accounting.financial-statements': [
            ['balanceSheet', 'STATEMENT_SUMMARY'],
            ['profitAndLoss', 'STATEMENT_SUMMARY'],
            ['cashFlow', 'STATEMENT_SUMMARY'],
        ],
        'report.client.statement': CLIENT_STATEMENT_BLOCKS,
        'report.organization.client-statements': CLIENT_STATEMENT_BLOCKS,
    };
    return (configured[reportId] ?? [])
        .filter(([field]) => summary[field] !== undefined)
        .map(([field, kind], order) => ({
        field,
        kind,
        labelKey: `reports.summary.${field}`,
        order,
        collapsedByDefault: true,
    }));
}
function currencySourceFor(kind) {
    return kind === 'MONEY' ? 'REPORTING_CURRENCY' : 'NONE';
}
function summaryMetricsFor(reportId, summary, structuredFields) {
    const primitiveFields = Object.keys(summary)
        .filter((field) => !structuredFields.has(field))
        .filter((field) => !TECHNICAL_FIELDS.has(field))
        .filter((field) => SUMMARY_LABEL_KEYS[field] && SUMMARY_VALUE_KINDS[field])
        .filter((field) => {
        const value = summary[field];
        return value === null
            || typeof value === 'string'
            || typeof value === 'number'
            || typeof value === 'boolean'
            || (Array.isArray(value) && value.every((item) => typeof item === 'string'));
    });
    const configured = PRIMARY_SUMMARY_FIELDS[reportId] ?? [];
    const configuredPrimary = configured.filter((field) => primitiveFields.includes(field)).slice(0, 4);
    const fallbackPrimary = configuredPrimary.length > 0 ? [] : primitiveFields.slice(0, 4);
    const primary = [...configuredPrimary, ...fallbackPrimary];
    const primarySet = new Set(primary);
    const remaining = primitiveFields.filter((field) => !primarySet.has(field));
    const metric = (field, order, prominence) => {
        const valueKind = SUMMARY_VALUE_KINDS[field];
        return {
            field,
            labelKey: SUMMARY_LABEL_KEYS[field],
            order,
            prominence,
            valueKind,
            currencySource: currencySourceFor(valueKind),
            precision: valueKind === 'PERCENT' || valueKind === 'MONEY' ? 2 : undefined,
        };
    };
    return [
        ...primary.map((field, order) => metric(field, order, 'PRIMARY')),
        ...remaining.map((field, index) => metric(field, 100 + index, 'DETAIL')),
    ];
}
export function inferReportColumnPresentation(field, sample) {
    let valueKind = 'TEXT';
    if (typeof sample === 'number')
        valueKind = 'NUMBER';
    else if (typeof sample === 'boolean')
        valueKind = 'TEXT';
    const sensitive = SENSITIVE_FIELDS.has(field);
    const technical = TECHNICAL_FIELDS.has(field);
    return {
        field,
        labelKey: `reports.columns.${field}`,
        valueKind,
        hiddenByDefault: technical,
        sensitive,
        exportable: !technical && !sensitive,
    };
}
const FIELD_TYPE_TO_VALUE_KIND = {
    text: 'TEXT',
    number: 'NUMBER',
    decimal: 'MONEY',
    quantity: 'NUMBER',
    money: 'MONEY',
    date: 'DATE',
    datetime: 'DATETIME',
    select: 'TEXT',
    status: 'STATUS',
    boolean: 'TEXT',
    link: 'REFERENCE',
    percentage: 'PERCENT',
    reference: 'REFERENCE',
};
export function declaredColumnPresentation(column) {
    const sensitive = column.privacyClassification === 'PII' || column.privacyClassification === 'CONFIDENTIAL';
    const technical = TECHNICAL_FIELDS.has(column.field);
    return {
        field: column.field,
        labelKey: String(column.labelKey),
        valueKind: column.fieldType
            ? (FIELD_TYPE_TO_VALUE_KIND[column.fieldType] ?? 'TEXT')
            : 'TEXT',
        hiddenByDefault: technical || column.defaultVisible === false,
        sensitive,
        exportable: column.exportConfig?.exportable === true
            || (!technical && !sensitive && column.defaultVisible !== false),
    };
}
export function getReportPresentationDescriptor(reportId, options = {}) {
    const group = groupFor(reportId, options.category);
    const summary = options.summary ?? {};
    const rows = options.rows ?? [];
    const declaredColumns = options.declaredColumns ?? [];
    const columns = declaredColumns.length > 0
        ? declaredColumns.map(declaredColumnPresentation)
        : (() => {
            const first = rows[0] ?? {};
            return Object.keys(first).map((field) => inferReportColumnPresentation(field, first[field]));
        })();
    const formats = exportFormats(reportId, group);
    const summaryBlocks = summaryBlocksFor(reportId, summary);
    const structuredFields = new Set(summaryBlocks.map((block) => block.field));
    return {
        descriptorVersion: 1,
        group,
        groupLabelKey: groupLabelKey(group),
        scopeLabelKey: scopeLabelKey(options.scope),
        hub: hubFor(reportId, group),
        exportFormats: formats,
        parameterKinds: {
            clientAccountId: 'CLIENT_REFERENCE',
            portfolioAccountId: 'PORTFOLIO_REFERENCE',
            branchId: 'TEXT',
            transactionStatus: 'ENUM',
            transactionType: 'ENUM',
            status: 'ENUM',
            type: 'ENUM',
            instrumentSymbol: 'TEXT',
            currency: 'TEXT',
            accountCode: 'TEXT',
            dimension: 'ENUM',
            from: 'DATE',
            to: 'DATE',
            fromDate: 'DATE',
            toDate: 'DATE',
            asOfDate: 'DATE',
            periodStart: 'DATE',
            periodEnd: 'DATE',
            period: 'PERIOD',
            fiscalPeriod: 'PERIOD',
        },
        summaryMetrics: summaryMetricsFor(reportId, summary, structuredFields),
        summaryBlocks,
        resultTable: {
            descriptorVersion: 1,
            columns,
            exportFormats: formats,
            clientSidePagination: false,
            clientSideSort: false,
            clientSideFilter: false,
            controls: {
                search: true,
                filters: true,
                columns: true,
                export: formats.includes('CSV'),
                density: false,
            },
        },
    };
}
